import type { RumEvent } from '@datadog/browser-rum';

import { isChunkLoadError } from './chunkError';

// Hosts CloudFront do RD Station: o loader carregado em TrackingTags e o rdtracker.min.js que ele
// injeta. Os mesmos hosts estao na allowlist de script-src da CSP (lib/security/csp-vendors.mjs).
const RD_STATION_SCRIPT_HOSTS = ['d335luupugsy2.cloudfront.net', 'd1sag09wwfbul8.cloudfront.net'] as const;

// O rdtracker.min.js referencia indexedDB e Notification sem checar a existencia dos globais. Em
// WebView restrita — navegador in-app do Instagram/Facebook, Safari privado — eles nao existem e o
// script lanca ReferenceError nao tratado, que vira ruido no Error Tracking. O codigo e de terceiro e
// minificado (nao corrigivel aqui) e o rastreio do RD precisa continuar, entao o evento e descartado.
export function isRdStationThirdPartyError(event: RumEvent): boolean {
  if (event.type !== 'error') return false;
  const origin = `${event.error.stack ?? ''}\n${event.error.resource?.url ?? ''}`;
  return RD_STATION_SCRIPT_HOSTS.some((host) => origin.includes(host));
}

// Keywords de CSP que representam bloqueio real (inline/eval/etc.) e devem continuar visiveis.
const MEANINGFUL_CSP_KEYWORDS = new Set([
  'inline',
  'eval',
  'wasm-eval',
  'unsafe-hashes',
  'self',
  'data',
  'blob',
  'filesystem',
]);

// IP privado/loopback/link-local ou host local: injecao de proxy/antivirus/WebView do cliente.
function isPrivateOrLocalHost(hostname: string): boolean {
  if (hostname === 'localhost' || hostname.endsWith('.local') || hostname.endsWith('.localhost')) return true;
  if (hostname === '::1' || /^127\./.test(hostname)) return true;
  if (/^10\./.test(hostname) || /^192\.168\./.test(hostname) || /^169\.254\./.test(hostname)) return true;
  const block172 = /^172\.(\d{1,3})\./.exec(hostname);
  return block172 !== null && Number(block172[1]) >= 16 && Number(block172[1]) <= 31;
}

// Violacao de CSP cujo recurso bloqueado nao da para allowlistar: um token opaco sem host
// (ex.: 'properties', que o WebView do Instagram reporta para uma chamada same-origin do nosso
// bundle que o proxy da Meta transforma em cross-origin) ou um host privado/loopback de proxy do
// cliente. Bloqueios com host publico (ex.: googlesyndication) seguem visiveis — esses a gente
// corrige liberando o host na CSP, nao escondendo.
export function isNonActionableCspReport(event: RumEvent): boolean {
  if (event.type !== 'error') return false;
  const match = /^csp_violation: '(.*?)' blocked by /.exec(event.error.message ?? '');
  if (!match) return false;
  const blocked = match[1];
  if (MEANINGFUL_CSP_KEYWORDS.has(blocked)) return false;
  try {
    return isPrivateOrLocalHost(new URL(blocked).hostname);
  } catch {
    return true;
  }
}

// Erro cross-origin opaco: o browser esconde o detalhe de script de outra origem sem CORS e so
// entrega "Script error." — sem arquivo, linha ou stack, nada acionavel.
export function isOpaqueCrossOriginScriptError(event: RumEvent): boolean {
  return event.type === 'error' && event.error.message === 'Script error.';
}

// Falha de chunk que o WebKit/Safari emite como rejeicao NAO tratada do runtime: o fetch do
// /_next/static/chunks/*.js e abortado de forma transiente (o arquivo responde 200 e na 2a tentativa
// carrega), nos primeiros segundos da pagina e sem derruba-la — o usuario continua navegando e ate
// conclui compra. E o maior volume de erro no RUM (~12% das sessoes de iOS) e nao e acionavel: o
// reload e contraindicado (recarregaria pagina que funciona) e o retry no import() e inerte no
// Turbopack (a promise de chunk rejeitada fica cacheada no runtime, re-chamar nao refaz o fetch).
// Os casos FATAIS, em que o chunk realmente sumiu e quebrou a pagina, sobem ao error boundary e sao
// reenviados por addNextjsError como evento `handled`, com dd_context.errorBoundary — esses seguem
// visiveis e sao a metrica do DEV-697. Portanto descartamos apenas o chunk-error `unhandled`: a
// fatia benigna e as duplicatas de runtime do caso fatal (a versao `handled`, com contexto, fica).
export function isUnhandledTransientChunkError(event: RumEvent): boolean {
  if (event.type !== 'error') return false;
  if (event.error.handling !== 'unhandled') return false;
  return isChunkLoadError({ name: event.error.type, message: event.error.message });
}

// Contrato do beforeSend do Datadog RUM: retornar false descarta o evento, true envia.
export function rumBeforeSend(event: RumEvent): boolean {
  if (isRdStationThirdPartyError(event)) return false;
  if (isNonActionableCspReport(event)) return false;
  if (isOpaqueCrossOriginScriptError(event)) return false;
  if (isUnhandledTransientChunkError(event)) return false;
  return true;
}
